import React, { useState } from 'react';
import { X, Plus, Star, ChevronUp, ChevronDown } from 'lucide-react';

const clienteName = (c) => c?.ragione_sociale || `${c?.nome || ''} ${c?.cognome || ''}`.trim() || 'Cliente';

/**
 * MultiClienteSelect: gestisce una lista ordinata di clienti.
 * - Il primo della lista è il cliente PRINCIPALE (badge "Principale").
 * - Tasti per riordinare / rimuovere.
 * - Bottone "+ Aggiungi cliente" con dropdown filtrabile dei clienti disponibili.
 *
 * Props:
 *   value: string[]  → array di cliente_id (ordinato)
 *   onChange: (newIds: string[]) => void
 *   clienti: Array<{id, ragione_sociale, nome, cognome, ...}>  → anagrafica completa
 *   placeholder?: string
 *   testid?: string
 */
export default function MultiClienteSelect({ value = [], onChange, clienti = [], placeholder = 'Seleziona cliente', testid = 'multi-cliente-select' }) {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');

  const selectedIds = value || [];
  const selectedClienti = selectedIds
    .map(id => clienti.find(c => c.id === id))
    .filter(Boolean);

  const available = clienti.filter(c => !selectedIds.includes(c.id) && (
    !search || clienteName(c).toLowerCase().includes(search.toLowerCase())
  ));

  const add = (id) => {
    onChange([...selectedIds, id]);
    setShowPicker(false);
    setSearch('');
  };
  const remove = (id) => onChange(selectedIds.filter(x => x !== id));
  const move = (idx, dir) => {
    const next = [...selectedIds];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };
  const makePrimary = (idx) => {
    if (idx === 0) return;
    const next = [...selectedIds];
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    onChange(next);
  };

  return (
    <div className="w-full" data-testid={testid}>
      {/* Lista clienti selezionati */}
      <div className="space-y-1.5">
        {selectedClienti.map((c, idx) => (
          <div key={c.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border ${idx === 0 ? 'bg-primary border-primary' : 'bg-slate-50 border-slate-200'}`} data-testid={`${testid}-item-${idx}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {idx === 0 && (
                  <span className="text-[9px] font-bold text-primary bg-primary px-1.5 py-0.5 rounded uppercase flex items-center gap-1">
                    <Star size={10} className="fill-current" /> Principale
                  </span>
                )}
                <span className="font-semibold text-sm truncate">{clienteName(c)}</span>
              </div>
              {(c.cf || c.piva) && <p className="text-[10px] text-slate-500 truncate">{c.piva && `P.IVA ${c.piva}`}{c.piva && c.cf && ' · '}{c.cf && `CF ${c.cf}`}</p>}
            </div>
            <div className="flex items-center gap-1">
              {selectedClienti.length > 1 && (
                <>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Sposta su" data-testid={`${testid}-move-up-${idx}`}>
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === selectedClienti.length - 1}
                    className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Sposta giù" data-testid={`${testid}-move-down-${idx}`}>
                    <ChevronDown size={14} />
                  </button>
                  {idx !== 0 && (
                    <button type="button" onClick={() => makePrimary(idx)}
                      className="p-1 text-primary hover:text-primary" title="Imposta come principale" data-testid={`${testid}-make-primary-${idx}`}>
                      <Star size={14} />
                    </button>
                  )}
                </>
              )}
              <button type="button" onClick={() => remove(c.id)}
                className="p-1 text-red-400 hover:text-red-600" title="Rimuovi" data-testid={`${testid}-remove-${idx}`}>
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
        {selectedClienti.length === 0 && (
          <p className="text-xs text-slate-400 italic px-3 py-2">{placeholder}</p>
        )}
      </div>

      {/* Bottone + picker */}
      {!showPicker ? (
        <button type="button" onClick={() => setShowPicker(true)}
          className="mt-2 w-full px-3 py-2 border-2 border-dashed border-slate-300 rounded-md text-xs font-bold text-slate-500 hover:border-primary hover:text-primary hover:bg-primary flex items-center justify-center gap-1.5 transition-colors"
          data-testid={`${testid}-add-btn`}>
          <Plus size={14} /> Aggiungi cliente
        </button>
      ) : (
        <div className="mt-2 border border-slate-200 rounded-md bg-white shadow-sm">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <input autoFocus type="text" placeholder="Cerca cliente..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="flex-1 px-2 py-1 border border-slate-200 rounded text-xs focus:ring-2 focus:ring-primary/40"
              data-testid={`${testid}-search`} />
            <button type="button" onClick={() => { setShowPicker(false); setSearch(''); }}
              className="text-slate-400 hover:text-slate-700">
              <X size={14} />
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {available.length === 0 && (
              <p className="text-xs text-slate-400 italic p-3 text-center">
                {clienti.length === 0 ? 'Nessun cliente in anagrafica' : 'Nessun cliente disponibile'}
              </p>
            )}
            {available.map(c => (
              <button key={c.id} type="button" onClick={() => add(c.id)}
                className="w-full text-left px-3 py-2 hover:bg-primary text-xs border-b border-slate-50 last:border-0"
                data-testid={`${testid}-option-${c.id}`}>
                <div className="font-semibold">{clienteName(c)}</div>
                {(c.cf || c.piva) && <div className="text-[10px] text-slate-400">{c.piva ? `P.IVA ${c.piva}` : `CF ${c.cf}`}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
